import { Button, Card, InputSelect } from '@features/ui';
import { RiseIcon } from '@features/ui/icons';
import { cn, formatDateByMST } from '@lib/utils.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import {
  confirmChoice,
  confirmToPa,
  downloadCertificate,
  getPaAccountCheckout,
  getPaAccountProcess,
  submitCreatePaAccountCheckout,
} from '@src/services/paAccountProcess.ts';
import {
  PaAccountProcess,
  PaPaymentMode,
  CheckoutPayload,
} from '@src/types/paAccountProcess.ts';
import { ProductOffer } from '@src/types';
import { AddonPerformanceAccountFee } from '@constants/addons';

export const CreatePaAccountPage = () => {
  const [data, setData] = useState<PaAccountProcess | null>(null);
  const [checkoutData, setCheckoutData] = useState<ProductOffer | null>(null);
  const [accountSelected, setAccountSelected] = useState<string | null>(null);

  const params = useParams();

  const leeloo_user_id = params.leeloo_user_id;
  const leeloo_account_id = params.leeloo_account_id;

  const fetchProcess = useCallback(async () => {
    if (!leeloo_user_id || !leeloo_account_id) return;
    const res = await getPaAccountProcess(leeloo_user_id, leeloo_account_id);

    setData(res.data);
  }, [leeloo_account_id, leeloo_user_id]);

  const fetchCheckout = useCallback(async () => {
    if (!leeloo_user_id || !leeloo_account_id) return;
    const res = await getPaAccountCheckout(leeloo_user_id, leeloo_account_id);

    setCheckoutData(res.data);
  }, [leeloo_account_id, leeloo_user_id]);

  const checkoutFeeText = useMemo(() => {
    if (!checkoutData) return '';
    if (
      checkoutData?.addon_performance_account_fee_option ===
      AddonPerformanceAccountFee.MONTHLY
    ) {
      const p = checkoutData?.billing_plan?.first_price;

      return p ? `$${p}/ month (pay if you qualify)` : '';
    }
    if (
      checkoutData?.addon_performance_account_fee_option ===
      AddonPerformanceAccountFee.LIFETIME_ONE_TIME
    ) {
      const p = checkoutData?.billing_plan?.first_price;

      return p ? `$${p} (only if you qualify)` : '';
    }

    return checkoutData?.description ?? '';
  }, [checkoutData]);

  const handleDownloadCertificate = useCallback(async () => {
    if (!leeloo_user_id || !leeloo_account_id) return;

    try {
      const response = await downloadCertificate(
        leeloo_user_id,
        leeloo_account_id
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download certificate:', error);
    }
  }, [leeloo_account_id, leeloo_user_id]);

  useEffect(() => {
    if (data?.stage2_complete) {
      fetchCheckout().then();
    }
  }, [data?.stage2_complete, fetchCheckout]);

  useEffect(() => {
    fetchProcess().then();

    const id = setInterval(fetchProcess, 30000);

    return () => {
      if (id) clearInterval(id);
    };
  }, [fetchProcess]);

  const handleConfirmChoice = useCallback(async () => {
    if (!leeloo_user_id || !leeloo_account_id || !accountSelected) return;

    await confirmChoice(
      leeloo_user_id,
      leeloo_account_id,
      Number(accountSelected)
    );
    await fetchProcess();
    setAccountSelected(null);
  }, [accountSelected, fetchProcess, leeloo_account_id, leeloo_user_id]);

  const [isCreatingCheckout, setCreatingCheckout] = useState(false);

  const handleCreateCheckout = useCallback(async () => {
    if (!leeloo_user_id || !leeloo_account_id || !checkoutData) return;

    setCreatingCheckout(true);
    try {
      if (
        checkoutData.addon_performance_account_fee_option ===
        AddonPerformanceAccountFee.LIFETIME_PAY_UPFRONT
      ) {
        await confirmToPa(leeloo_user_id, leeloo_account_id);
        toast.success('Confirmed PA creation (pay upfront).');
        await fetchProcess();

        return;
      }

      const payment_mode =
        checkoutData.addon_performance_account_fee_option ===
        AddonPerformanceAccountFee.MONTHLY
          ? PaPaymentMode.SUBSCRIPTION
          : PaPaymentMode.ONETIME;

      const packageId = checkoutData.id;

      const payload: CheckoutPayload = {
        leeloo_user_uuid: leeloo_user_id,
        leeloo_account_uuid: leeloo_account_id,
        payment_mode,
        payment_gateway: 'stripe',
        package_id: packageId ? Number(packageId) : undefined,
        billing_info: {
          street: '',
          city: '',
          country: '',
          state: '',
          zip_code: '',
          phone_number: '',
          first_name: data?.leeloo_user?.first_name ?? '',
          last_name: data?.leeloo_user?.last_name ?? '',
        },
      };

      const res = await submitCreatePaAccountCheckout(payload);

      if (res.data?.url) {
        window.open(res.data.url, '_blank');
      } else {
        toast.error(
          'Unable to create checkout. Please try the full checkout flow.'
        );
      }
    } catch (err) {
      toast.error('Something went wrong creating checkout.');
    } finally {
      setCreatingCheckout(false);
    }
  }, [leeloo_account_id, leeloo_user_id, checkoutData, data]);

  return (
    <div className={'flex flex-col xl:px-40 xl:gap-10 gap-5 py-10'}>
      <div className={'flex flex-col gap-2'}>
        <h1 className={'text-[23px] font-medium text-white'}>
          Performance Account Creation
        </h1>
        <div className={'flex justify-between'}>
          <div className={'flex flex-col gap-2'}>
            <h2 className={'text-sm font-medium text-gray-300'}>
              Hi {data?.leeloo_user?.first_name} {data?.leeloo_user?.last_name}{' '}
              - congratulations on your trading performance on account{' '}
              <b className="text-blue-400">{data?.leeloo_user.username}</b>.
            </h2>
            <h2 className={'text-sm font-medium text-gray-300'}>
              You have been approved to progress to a Performance Trading
              account!
            </h2>
          </div>
          <img
            alt="rithmic"
            height={84}
            src="/assets/logo-rithmic.png"
            width={290}
          />
        </div>
        <Button
          className={'w-fit'}
          variant={'secondary'}
          onClick={handleDownloadCertificate}
        >
          CERTIFICATE OF ACHIEVEMENT
        </Button>
      </div>
      <Card>
        <p className={'text-[23px] font-medium text-white'}>What next?</p>
        <p className={'text-sm font-medium text-gray-300'}>
          Please complete the following steps in order to activate your
          Performance Account
        </p>
        <div className="mt-4 flex flex-col gap-5">
          {/*stage 1*/}
          {data?.stage1_complete ? (
            <div
              className={
                'bg-green-900/30 bg-opacity-30 border border-green-800 px-[20px] py-[12px] text-green-200 flex gap-4 items-center'
              }
            >
              <img
                alt="next step"
                height={60}
                src="/assets/icon-complete.png"
                width={60}
              />
              <div className={'flex-1'}>
                <p className="text-white">
                  1. Your selected Performance account type
                </p>
                <p className={'font-bold text-white py-2'}>
                  Performance account - {data.pa_product.title}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Completed on:{' '}
                  {formatDateByMST(
                    data?.leeloo_account.qualify_pa_account_package_date
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div
              className={
                'bg-blue-900/30 bg-opacity-30 border border-blue-800 px-[20px] py-[12px] text-white flex items-center gap-4'
              }
            >
              <img
                alt="next step"
                height={60}
                src="/assets/icon-next.png"
                width={60}
              />
              <div className="flex-1">
                <p className="text-white">
                  1. Choose your Performance account type
                </p>
                <div className="mt-2">
                  <InputSelect
                    className="bg-gray-700 border-gray-600 text-white"
                    options={
                      data?.pa_product
                        ? [
                            {
                              label: data?.pa_product.title ?? '',
                              value: data?.pa_product.id.toString() ?? '',
                            },
                          ]
                        : []
                    }
                    onChange={(value) => setAccountSelected(value)}
                  />
                </div>
                <p className="text-sm mt-2">
                  You can NOT change your mind once selected
                </p>
              </div>
              <Button
                className="w-fit"
                disabled={!accountSelected}
                variant="lime"
                onClick={handleConfirmChoice}
              >
                CONFIRM CHOICE
              </Button>
            </div>
          )}

          {/*stage 2*/}

          {data?.stage2_complete ? (
            <div
              className={
                'bg-green-900/30 bg-opacity-30 border border-green-800 px-[20px] py-[12px] text-green-200 flex gap-4'
              }
            >
              <img
                alt="next step"
                height={60}
                src="/assets/icon-complete.png"
                width={60}
              />
              <div className={'flex-1'}>
                <p className="text-white">
                  2. You have completed your Performance & Payout Agreement with
                  Rise
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Completed on:{' '}
                  {formatDateByMST(data.leeloo_user.rise_works_confirmed)}
                </p>
              </div>
              <RiseIcon />
            </div>
          ) : (
            <div
              className={
                'bg-blue-900/30 bg-opacity-30 border border-blue-800 px-[20px] py-[12px] text-white flex items-center gap-4'
              }
            >
              <img
                alt="next step"
                height={60}
                src="/assets/icon-next.png"
                width={60}
              />
              <div className="flex-1">
                <p className="text-white">
                  2. Sign our Performance & Payout Agreement with Rise
                </p>
                <p className={'text-sm mt-2'}>
                  {`All PA users must register with our external partner,
                        'Rise', and sign our Performance agreement. We make all
                        account payouts via 'Rise'.`}
                </p>
                {data?.leeloo_user.rise_works_invited ? (
                  <p className={'text-sm mt-2'}>
                    <strong>
                      {`You should have received an email from 'Rise' on ${new Date(data.leeloo_user.rise_works_invited).toLocaleString()} - please follow the instructions in the email, register and sign the agreement, then return to this page to purchase your PA account.`}
                    </strong>
                  </p>
                ) : (
                  <p className={'text-sm mt-2'}>
                    <strong>
                      {`You will shortly receive an email from 'Rise' - please
                          follow the instructions, register and sign the agreement,
                          then return to this page to purchase your PA account.`}
                    </strong>
                  </p>
                )}
              </div>
              <RiseIcon />
            </div>
          )}

          {/*stage3*/}
          <div
            className={cn(
              'flex items-center gap-4 px-[20px] py-[12px]px-[20px] py-[12px]',
              data?.stage1_complete && data.stage2_complete
                ? 'bg-blue-900/30 border-blue-800'
                : 'text-[#898989] blur-[4px] relative bg-[gray] border border-gray-700'
            )}
          >
            <img
              alt={data?.stage3_complete ? 'Completed' : 'Pending'}
              height={60}
              src={
                data?.stage3_complete
                  ? '/assets/icon-complete.png'
                  : '/assets/icon-pending.png'
              }
              width={60}
            />

            <div className="flex-1">
              <p className="text-white font-medium">
                3.{' '}
                {data?.stage3_complete
                  ? 'Performance Account Active'
                  : 'Purchase Subscription'}
              </p>
              {data?.stage3_complete ? (
                <p className="text-sm text-gray-300 mt-2">
                  Your new performance account is ready! You can now start
                  trading.
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-300 mt-2">
                    Please login to our{' '}
                    <a
                      className="text-blue-400 hover:underline"
                      href={data?.our_website ?? ''}
                      rel="noreferrer"
                      target="_blank"
                    >
                      website here
                    </a>{' '}
                    with your email{' '}
                    <span className="font-medium text-blue-100">
                      {data?.leeloo_user?.email}
                    </span>
                    , <strong>once logged in</strong> you can purchase one of
                    these subscriptions:
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <Button
                      className="w-full md:w-fit h-auto flex flex-col items-start"
                      disabled={isCreatingCheckout}
                      variant="secondary"
                      onClick={handleCreateCheckout}
                    >
                      <div>{checkoutData?.title}</div>
                      {checkoutFeeText.trim() ? (
                        <div className="font-normal text-[12px]">
                          {checkoutFeeText}
                        </div>
                      ) : null}
                    </Button>
                  </div>
                  <p className="text-xs text-white mt-5">
                    Please allow 10 minutes between purchasing your subscription
                    and the account being activated - you can check back on this
                    page for updates.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
